import React, { useState, useContext } from 'react';
import ReactDOM from 'react-dom';
import { DialogContext } from '../context/DialogContext.js';
import UserContext from '../context/UserContext.js';
import { useMutation, gql } from '@apollo/client';
import styles from './AddProjectDialog.module.scss';
import CloseIcon from '@mui/icons-material/Close';

// Define the GraphQL mutation
const ADD_PROJECT_MUTATION = gql`
  mutation AddProject(
    $username: String!
    $title: String!
    $code: String!
    $description: String!
    $icon: String
  ) {
    addProject(
      input: {
        username: $username
        title: $title
        code: $code
        description: $description
        icon: $icon
      }
    ) {
      identity
      labels
      properties {
        title
        code
        description
        icon
        dateAdded
        dateLastModified
        username
      }
      elementId
    }
  }
`;

function AddProjectDialog() {
  const { dialogStates, closeDialog } = useContext(DialogContext);
  const { username } = useContext(UserContext);
  
  const [projectTitle, setProjectTitle] = useState('');
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('');
  
  const [addProject, { loading }] = useMutation(ADD_PROJECT_MUTATION, {
    onCompleted: () => {
      setProjectTitle('');
      setCode('');
      setDescription('');
      setIcon('');
      handleClose();
    }
  });

  const isOpen = dialogStates['dialogAddProject'];

  const handleClose = () => {
    closeDialog('dialogAddProject');
  };

  const handleSave = async () => {
    if (!projectTitle || !code || !description) {
      return;
    }

    try {
      await addProject({
        variables: {
          username,
          title: projectTitle,
          code,
          description,
          icon: icon || undefined,
        },
      });
    } catch (err) {
      console.error('Error adding project:', err);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={handleClose}>
          <CloseIcon />
        </button>
        
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>Architecture</div>
            <h2 className={styles.title}>New Project</h2>
          </div>
        </div>

        <div className={styles.container}>
          <div className={styles.field}>
            <div className={styles.fieldLabel}>Project Title</div>
            <input 
              autoFocus
              type="text" 
              className={styles.input} 
              value={projectTitle} 
              onChange={(e) => setProjectTitle(e.target.value)}
              placeholder="e.g. Language Graph"
            />
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Project Code</div>
            <input 
              type="text" 
              className={styles.input} 
              value={code} 
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. LG-01"
            />
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Description</div>
            <textarea 
              className={styles.input} 
              value={description} 
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this project about?"
            />
          </div>

          <div className={styles.field}>
            <div className={styles.fieldLabel}>Icon (Optional)</div>
            <input 
              type="text" 
              className={styles.input} 
              value={icon} 
              onChange={(e) => setIcon(e.target.value)}
              placeholder="🚀, 🌐, 📚..."
            />
          </div>
        </div>

        <div className={styles.footer}>
          <button 
            className={styles.primaryBtn} 
            onClick={handleSave} 
            disabled={loading || !projectTitle || !code || !description}
          >
            {loading ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default AddProjectDialog;